import { getMemberBookedSpotName, mergeSnakeCamelBookedSpots } from './bookedSpotNames';
import { getMemberBookedSpotNameFromWire } from './bookedSpotNamesWire';
import type {
  BookSpotReserveMeta,
  BookSpotReserveParams,
  InteractiveSpotBookingParamsLike
} from './types';
import { trimStringOrEmpty } from './spotName';

export const BOOK_SPOT_SUCCESS_TRIGGER = 'Success';
export const BOOK_SPOT_SUCCESS_DELAY_MS = 100;
export const BOOK_SPOT_RESERVE_TYPE = 'booked';
export const BOOK_SPOT_RESERVE_ACTION = 'Home';

function getBookedSpotsFromParams(
  params: InteractiveSpotBookingParamsLike
): InteractiveSpotBookingParamsLike['booked_spots'] {
  const hasSnake = Array.isArray(params.booked_spots);
  const hasCamel = Array.isArray(params.bookedSpots);
  if (!hasSnake && !hasCamel) {
    return undefined;
  }
  return mergeSnakeCamelBookedSpots(params.booked_spots, params.bookedSpots);
}

/**
 * Explicit navigation / hydration fields (highest priority).
 * `previousSpotName` — change-spot route param for the spot being replaced.
 * `currentBookedSpotName` — disambiguates multi-seat wire arrays (member vs buddy).
 * `memberBookedSpotName` — hydrated member spot when arrays would be ambiguous.
 */
export function getExplicitMemberSpotFromParams(params: InteractiveSpotBookingParamsLike): string {
  return (
    trimStringOrEmpty(params.previousSpotName) ||
    trimStringOrEmpty(params.currentBookedSpotName) ||
    trimStringOrEmpty(params.memberBookedSpotName) ||
    ''
  );
}

/** Wire and structured booked-spot arrays when explicit fields are absent. */
export function getLegacyBookedSpotFromParams(params: InteractiveSpotBookingParamsLike): string {
  return (
    getMemberBookedSpotName(getBookedSpotsFromParams(params)) ||
    getMemberBookedSpotNameFromWire(params.booked_spot_names) ||
    getMemberBookedSpotNameFromWire(params.bookedSpotNames) ||
    ''
  );
}

export function getPreviousSpotIdFromParams(params: InteractiveSpotBookingParamsLike): string {
  return getExplicitMemberSpotFromParams(params) || getLegacyBookedSpotFromParams(params);
}

/** Same resolution order as executeBookSpot: action-level id first, then route params. */
export function resolvePreviousSpotIdForChangeSpot(
  params: InteractiveSpotBookingParamsLike,
  previousSpotIdOverride?: string
): string {
  return trimStringOrEmpty(previousSpotIdOverride) || getPreviousSpotIdFromParams(params);
}

export function hasExistingBookedSpot(params: InteractiveSpotBookingParamsLike): boolean {
  return getPreviousSpotIdFromParams(params) !== '';
}

/**
 * True when a change-spot flow is really a first spot assignment — the booking's member currently
 * holds no spot, so an empty previous spot is expected (reserve without `old_spot_name`) rather than
 * a failed swap. Without it, an empty previous spot is treated as a missing-spot error to guard
 * assigned bookings against double-booking.
 *
 * The app sets `isFirstSpotAssignment` from the booking payload. The rule is only about the member
 * slot (`guest === 0`, wire index `MEMBER_BOOKED_SPOT_INDEX`); buddy/guest spots never make a
 * booking assigned. Derive it with `isMemberBookedSpotUnassignedFromBookedSpots` (structured
 * payloads) or `isMemberBookedSpotUnassignedFromWire` (`booked_spot_names`) rather than re-deriving,
 * which yields exactly these shapes:
 *
 * | Booking payload                                             | First assignment |
 * | ----------------------------------------------------------- | ---------------- |
 * | `booked_spots: []` / `booked_spot_names: []`                 | yes              |
 * | `[{ guest: 0, spot_name: '' }]` / `['']`                     | yes              |
 * | `[{ guest: 0, spot_name: '' }, { guest: 1, ... }]` / `['', 'B02']` | yes        |
 * | `[{ guest: 1, spot_name: 'B02' }]` (member slot absent)      | yes              |
 * | `[{ guest: 0, spot_name: 'R05' }]` / `['R05']`               | no (real swap)   |
 * | `[{ guest: 0, spot_name: 'R05' }, { guest: 1, ... }]` / `['R05', 'B02']` | no    |
 *
 * A member spot name on the params contradicts the flag; `resolveOldSpotNameForReserve` keeps the
 * flag's meaning and warns in dev.
 */
export function isFirstSpotAssignment(params: InteractiveSpotBookingParamsLike): boolean {
  return params.isFirstSpotAssignment === true;
}

/** After a failed reserve, keep an existing failed label or fall back to the attempted grid key. */
export function resolveLastFailedSpotName(
  prev: string | null,
  attemptedSpotName: string | null
): string | null {
  if (prev) {
    return prev;
  }
  if (attemptedSpotName) {
    return attemptedSpotName;
  }
  return null;
}

export function shouldTrackChangeSpotSuccess(args: {
  wasBookingLoading: boolean;
  isBookingLoading: boolean;
  hasBookSpotError: boolean;
  isChangeSpotFlow: boolean;
}): boolean {
  const { wasBookingLoading, isBookingLoading, hasBookSpotError, isChangeSpotFlow } = args;
  return wasBookingLoading && !isBookingLoading && !hasBookSpotError && isChangeSpotFlow;
}

type BookSpotSuccessTriggerParams = Pick<InteractiveSpotBookingParamsLike, 'isChangeSpotFlow'>;

export function getBookSpotSuccessTrigger(
  isQuickBook: boolean,
  params?: BookSpotSuccessTriggerParams
): typeof BOOK_SPOT_SUCCESS_TRIGGER | undefined {
  if (isQuickBook || params?.isChangeSpotFlow === true) {
    return undefined;
  }
  return BOOK_SPOT_SUCCESS_TRIGGER;
}

/**
 * `isChangeSpotFlowOverride` explicitly wins when provided (including `false`);
 * `undefined` falls back to `params.isChangeSpotFlow`.
 */
export function resolveIsChangeSpotFlow(
  params: InteractiveSpotBookingParamsLike,
  isChangeSpotFlowOverride?: boolean
): boolean {
  return isChangeSpotFlowOverride ?? params.isChangeSpotFlow === true;
}

export function buildBookSpotReserveMeta(
  isQuickBook: boolean,
  params: InteractiveSpotBookingParamsLike,
  bookParams: BookSpotReserveParams,
  resolvedPreviousSpotId = '',
  isChangeSpotFlowOverride?: boolean
): BookSpotReserveMeta<BookSpotReserveParams> {
  const isChangeSpotFlow = resolveIsChangeSpotFlow(params, isChangeSpotFlowOverride);
  const previousSpotId =
    trimStringOrEmpty(resolvedPreviousSpotId) ||
    (isChangeSpotFlow ? getPreviousSpotIdFromParams(params) : '');

  // A change-spot reserve navigates to the Success screen both when swapping an existing spot
  // (previousSpotId) and when assigning a spot for the first time on an unassigned booking
  // (isFirstSpotAssignment) — otherwise the first-assignment flow would silently close with no
  // success screen or in-place list refresh.
  const isChangeSpotSuccess =
    isChangeSpotFlow && (previousSpotId !== '' || isFirstSpotAssignment(params));
  if (!isQuickBook && isChangeSpotSuccess) {
    return {
      trigger: BOOK_SPOT_SUCCESS_TRIGGER,
      delay: BOOK_SPOT_SUCCESS_DELAY_MS,
      params: {
        ...bookParams,
        type: BOOK_SPOT_RESERVE_TYPE,
        isChangeSpotFlow: true,
        action: BOOK_SPOT_RESERVE_ACTION
      }
    };
  }

  const trigger = getBookSpotSuccessTrigger(
    isQuickBook,
    isChangeSpotFlow ? { isChangeSpotFlow: true } : undefined
  );
  if (!trigger) {
    return { params: bookParams };
  }
  return { trigger, params: { ...bookParams, type: BOOK_SPOT_RESERVE_TYPE } };
}
